'use client';

import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { cn } from '@/libs/utils';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger } from '@/components/ui/navigation-menu';

const modelLinks = [
  { href: '/wan22', label: 'Wan 2.2' },
  { href: '/wan/2-5', label: 'Wan 2.5' },
  { href: '/wan/2-6', label: 'Wan 2.6' },
  { href: '/wan/2-7', label: 'Wan 2.7' },
  { href: '/wan/3-0', label: 'Wan 3.0' },
];

const videoTools = [
  { href: '/wan22', labelKey: 'VideoWithAudio' },
  { href: '/speech-to-video', labelKey: 'SpeechToVideo' },
  { href: '/image-to-video', labelKey: 'ImageToVideo' },
  { href: '/text-to-video', labelKey: 'TextToVideo' },
  { href: '/video-to-video', labelKey: 'VideoToVideo' },
  { href: '/video-extend', labelKey: 'VideoExtend' },
  { href: '/reference-to-video', labelKey: 'ReferenceToVideo' },
  { href: '/wan22-animate', labelKey: 'Wan22Animate' },
];
const imageTools = [
  { href: '/text-to-image', labelKey: 'TextToImage' },
  { href: '/image-to-image', labelKey: 'ImageToImage' },
];

type MenuGroup = { label?: string; links: { href: string; label: string }[] };

function useGeneratorMenus(): { id: string; label: string; groups: MenuGroup[] }[] {
  const t = useTranslations('Header');
  const translateTools = (tools: typeof videoTools) => tools.map(tool => ({ href: tool.href, label: t(tool.labelKey) }));
  return [
    { id: 'models', label: t('Models'), groups: [
      { links: modelLinks },
      { links: [{ href: '/wan', label: t('AllModels') }] },
    ] },
    { id: 'tools', label: t('Tools'), groups: [
      { label: t('VideoTools'), links: translateTools(videoTools) },
      { label: t('ImageTools'), links: translateTools(imageTools) },
    ] },
  ];
}

const linkClass = 'block rounded-md px-3 py-2.5 text-sm text-gray-300 transition-colors hover:bg-green-500/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400';

export function DesktopGeneratorMenus() {
  const menus = useGeneratorMenus();
  return <>{menus.map(menu => <NavigationMenuItem key={menu.id} value={menu.id}>
    <NavigationMenuTrigger className="h-auto bg-transparent px-0 py-2 text-sm font-medium text-gray-300 hover:bg-transparent hover:text-white focus:bg-transparent focus:text-white data-[state=open]:bg-transparent data-[state=open]:text-white xl:text-base">
      {menu.label}
    </NavigationMenuTrigger>
    <NavigationMenuContent>
      <div className={cn('flex flex-col gap-3 bg-slate-950 p-3', menu.id === 'tools' ? 'w-[440px]' : 'w-56')}>
        {menu.groups.map((group, index) => <div key={index} className={index > 0 ? 'border-t border-green-400/20 pt-3' : undefined}>
          {group.label && <p className="mb-1 px-3 text-xs font-medium text-gray-400">{group.label}</p>}
          <ul className={menu.id === 'tools' ? 'grid grid-cols-2 gap-1' : 'flex flex-col gap-1'}>
            {group.links.map(link => <li key={link.href}>
              <NavigationMenuLink asChild><Link href={link.href} className={linkClass}>{link.label}</Link></NavigationMenuLink>
            </li>)}
          </ul>
        </div>)}
      </div>
    </NavigationMenuContent>
  </NavigationMenuItem>)}</>;
}

export function MobileGeneratorMenus({ onNavigate }: { onNavigate: () => void }) {
  const menus = useGeneratorMenus();
  return <Accordion type="multiple" className="flex flex-col gap-1">
    {menus.map(menu => <AccordionItem key={menu.id} value={menu.id} className="rounded-xl border border-green-400/20 bg-black/30">
      <AccordionTrigger className="px-3 py-3 text-gray-200 hover:text-white hover:no-underline sm:px-4 sm:py-4">{menu.label}</AccordionTrigger>
      <AccordionContent className="flex flex-col gap-3 px-2 pb-2">
        {menu.groups.map((group, index) => <div key={index} className={index > 0 ? 'border-t border-green-400/20 pt-3' : undefined}>
          {group.label && <p className="mb-1 px-3 text-xs font-medium text-gray-400">{group.label}</p>}
          <ul className="flex flex-col gap-1">{group.links.map(link => <li key={link.href}>
            <Link href={link.href} onClick={onNavigate} className={linkClass}>{link.label}</Link>
          </li>)}</ul>
        </div>)}
      </AccordionContent>
    </AccordionItem>)}
  </Accordion>;
}
